import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import { useTranslation } from '~/hooks/useTranslation'
import { apiGet, apiPost, getApi } from '~/util/api'
import { Button, Card, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import { SpinnerWhite } from '~/components/shared/Spinner'
import CoachInfoDialog from '../CoachInfoDialog'
import { useBenefits } from '../../coach_services/BenefitsProvider'
import HeadshotCircle from '~/components/coach_services/MeetYourCoach/HeadshotCircle'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { useFormatTime } from '~/hooks/useFormatTime'
import YnsIcon from '~/components/icons/YnsIcon'
import safeParse from '~/lib/safeParse'
import htmlTruncate from 'html-truncate'
import { useToast, Toaster } from '~/lib/ui/toast'

export function SelectCoachCard({
  className,
  color = 'default',
  variant = 'default',
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [coachInfo, setCoachInfo] = useState(null)
  const [scheduledIntakeCall, setScheduledIntakeCall] = useState(null)

  useEffect(() => {
    setIsLoading(true)
    getApi()
      .get('/coach_info')
      .then((response) => {
        if (response.status == 200) {
          setCoachInfo(response.data)

          if (response.data.scheduled_intake_call)
            setScheduledIntakeCall(response.data.scheduled_intake_call)
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }, [setCoachInfo])

  return (
    <Card
      className={`w-full max-w-full overflow-x-hidden ${className || ''}`}
      color={color}
      variant={variant}
    >
      <Stack className="h-full w-full" spacing={8}>
        {isLoading || _.isEmpty(coachInfo) ? (
          <div className="flex w-full items-center justify-center">
            <SpinnerWhite show className="h-9 w-9" />
          </div>
        ) : (
          <>
            {coachInfo?.liaison ? (
              <LiaisonCard
                coach={coachInfo}
                scheduledIntakeCall={scheduledIntakeCall}
                setScheduledIntakeCall={setScheduledIntakeCall}
              />
            ) : (
              <DedicatedCoachCard coach={coachInfo} />
            )}
          </>
        )}
      </Stack>
      <Toaster />
    </Card>
  )
}

function LiaisonCard({ coach, scheduledIntakeCall, ...props }) {
  const { t } = useTranslation()
  const numToIncrement = 4
  const [numToShow, setNumToShow] = useState(numToIncrement)
  const [failedSlotKeys, setFailedSlotKeys] = useState(() => new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['availableTimes', scheduledIntakeCall],
    queryFn: async () => {
      if (scheduledIntakeCall) return []
      const response = await apiGet(`/available_times`).catch((error) => {
        console.error('Error fetching available times:', error)
        return { data: { available_times: [] } }
      })
      return response.data
    },
  })

  const availableTimes = data?.available_times
  const slotKey = (availability) =>
    `${availability.start_time}-${availability.event_type}-${availability.coach_id}`
  const availableTimesFiltered =
    availableTimes?.filter(
      (availability) => !failedSlotKeys.has(slotKey(availability)),
    ) || []

  const availableTimesDisplay = availableTimesFiltered.slice(0, numToShow)
  const coachImage =
    scheduledIntakeCall?.headshot ||
    scheduledIntakeCall?.image ||
    'https://www-yns-com.s3.us-east-2.amazonaws.com/images/coach_services/exec_team.jpg'

  useEffect(() => {
    if (scheduledIntakeCall) {
      setFailedSlotKeys(new Set())
    }
  }, [scheduledIntakeCall])

  const handleSlotSchedulingError = (availability) => {
    const key = slotKey(availability)
    setFailedSlotKeys((prev) => {
      const next = new Set(prev)
      next.add(key)
      return next
    })
  }

  return (
    <div className="grid w-full max-w-full grid-cols-1 items-center gap-4 overflow-x-hidden p-4 sm:p-8 xl:grid-cols-[1fr_auto]">
      <div className="flex w-full max-w-full flex-col items-start gap-4 xl:flex-row xl:items-stretch xl:gap-8">
        <div className="flex min-w-0 flex-col items-center gap-4">
          <HeadshotCircle coachImage={coachImage} variant="selectDashboard" />
          <Typography
            color="white"
            className="w-full max-w-xs break-words text-center"
          >
            {scheduledIntakeCall
              ? t('select_liaison_coach_card.your_exec_concierge')
              : t('select_liaison_coach_card.exec_concierge')}
          </Typography>
        </div>
        <Stack
          spacing={8}
          className="w-full max-w-full xl:border-l xl:border-white xl:pl-8"
        >
          <Typography
            variant="title4"
            color="white"
            component="h2"
            className="w-full break-words"
          >
            {t('select_liaison_coach_card.name')}
          </Typography>
          <Typography color="white" className="w-full break-words">
            {scheduledIntakeCall
              ? t('select_liaison_coach_card.description_scheduled')
              : t('select_liaison_coach_card.description', {
                  name: coach.name,
                })}
          </Typography>
          {scheduledIntakeCall ? (
            <ScheduledIntakeCall
              scheduledIntakeCall={scheduledIntakeCall}
              {...props}
            />
          ) : (
            <>
              <Typography color="white" className="w-full break-words">
                {t('select_liaison_coach_card.schedule_call')}
              </Typography>
              {isLoading ? (
                <div className="flex w-full items-center gap-2">
                  <Typography color="white">
                    {t('select_liaison_coach_card.getting_available_times')}
                  </Typography>
                  <SpinnerWhite show className="h-9 w-9" />
                </div>
              ) : (
                <>
                  <Stack spacing={1} className="w-full items-start">
                    {!isLoading && availableTimesFiltered.length === 0 && (
                      <Typography color="white">
                        {t(
                          'select_liaison_coach_card.no_available_times_error',
                        )}
                      </Typography>
                    )}
                    <div className="flex w-full flex-wrap gap-2">
                      {availableTimesDisplay.map((availability, idx) => (
                        <div
                          key={`intake-schedule-${availability.start_time}-${availability.event_type}-${availability.coach_id || idx}`}
                          className="w-full min-w-0 flex-shrink sm:w-auto"
                        >
                          <IntakeCallScheduling
                            availability={availability}
                            onSchedulingError={handleSlotSchedulingError}
                            {...props}
                          />
                        </div>
                      ))}
                    </div>
                  </Stack>
                  <div className="flex w-full flex-wrap items-center gap-4">
                    {availableTimesFiltered.length > numToShow && (
                      <Button
                        variant="outline"
                        color="secondary"
                        onClick={() => setNumToShow(numToShow + numToIncrement)}
                        className="flex-shrink"
                      >
                        {t('select_liaison_coach_card.view_more')}
                      </Button>
                    )}
                    {numToShow > numToIncrement && (
                      <Button
                        variant="outline"
                        color="secondary"
                        onClick={() => setNumToShow(numToIncrement)}
                        className="flex-shrink"
                      >
                        {t('select_liaison_coach_card.view_less')}
                      </Button>
                    )}
                    <a
                      href="/coach_services/meet_your_coach"
                      className="flex-shrink !text-white !underline"
                    >
                      {t('select_liaison_coach_card.view_action')}
                    </a>
                  </div>
                </>
              )}
            </>
          )}
        </Stack>
      </div>
      <CoachInfoDialog coach={coach} />
    </div>
  )
}

const ScheduledIntakeCall = ({
  scheduledIntakeCall,
  setScheduledIntakeCall,
}) => {
  const { current_user } = useBenefits()
  const { t } = useTranslation('select_liaison_coach_card')
  const formatTime = useFormatTime(current_user.locale)

  const startTime = new Date(scheduledIntakeCall.start_time)

  const formattedTime = formatTime(startTime, 'meeting_datetime')

  const queryClient = useQueryClient()

  const onCancelClick = () => {
    let intervalId

    const rerunQueries = async () => {
      try {
        const res = await apiGet(
          '/check_for_rescheduled_or_cancelled_intake_call',
        )
        if (res.data.available_times) {
          queryClient.invalidateQueries(['availableTimes', scheduledIntakeCall])
          clearInterval(intervalId)
        }
        if (!res.data.scheduled_intake_call) {
          setScheduledIntakeCall(null)
          clearInterval(intervalId)
        }
        if (res.data.scheduled_intake_call?.id !== scheduledIntakeCall.id) {
          setScheduledIntakeCall(res.data.scheduled_intake_call)
          clearInterval(intervalId)
        }
        return
      } catch (error) {
        console.error('Error fetching Calendly events:', error)
      }
    }

    intervalId = setInterval(rerunQueries, 4000)

    return () => clearInterval(intervalId)
  }

  return (
    <Stack spacing={8} className="items-start">
      <Typography variant="body2" color="secondary" className="w-full">
        <div className="w-full rounded-lg bg-willow-brook p-3 text-left font-medium">
          {formattedTime}
        </div>
      </Typography>
      <div className="flex items-center gap-4 text-left">
        <Button
          href={scheduledIntakeCall.join_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="outline"
          color="primary"
          endIcon={<YnsIcon name="video" className="ml-2" />}
        >
          {t('join_meeting')}
        </Button>
        <Button
          href={scheduledIntakeCall.reschedule_url}
          onClick={onCancelClick}
          target="_blank"
          rel="noopener noreferrer"
          variant="link"
          className="!text-white !underline"
        >
          {t('reschedule')}
        </Button>
        <Button
          href={scheduledIntakeCall.cancel_url}
          onClick={onCancelClick}
          target="_blank"
          rel="noopener noreferrer"
          variant="link"
          className="!text-white !underline"
        >
          {t('cancel')}
        </Button>
      </div>
      <div>
        <a
          href="/coach_services/meet_your_coach"
          className="!text-white !underline"
        >
          {t('view_action')}
        </a>
      </div>
    </Stack>
  )
}

function DedicatedCoachCard({ coach }) {
  const { t, locale } = useTranslation()
  const formatTime = useFormatTime(locale)
  const truncatedFeedback = htmlTruncate(
    coach.latest_feedback?.feedback || '',
    200,
  )
  const quotedFeedback = wrapQuotesInParagraphs(truncatedFeedback)

  const scheduledCall = coach.scheduled_coaching_call
  const callScheduled = Boolean(scheduledCall?.start_time)
  const scheduledDate = callScheduled
    ? formatTime(scheduledCall.start_time, 'short')
    : null

  return (
    <div className="grid grid-cols-1 items-center gap-4 p-8 xl:grid-cols-[1fr_auto]">
      <div className="flex flex-col items-start gap-4 xl:flex-row xl:items-stretch xl:gap-8">
        <div className="flex flex-col items-center gap-4">
          <HeadshotCircle
            coachImage={coach.headshot || coach.image}
            variant="selectDashboard"
          />
          <Typography color="white">
            {t('select_dedicated_coach_card.your_exec_coach')}
          </Typography>
        </div>
        <Stack
          spacing={8}
          className="w-full xl:border-l xl:border-white xl:pl-8"
        >
          <Typography variant="title4" color="white" component="h2">
            {t('select_dedicated_coach_card.name')}
          </Typography>
          <div className="grid w-full gap-8 lg:grid-cols-2">
            <CardTile
              title={
                callScheduled
                  ? coach.orientation_call_completed
                    ? t('service_card.scheduled')
                    : t('premium_dedicated_coach_card.coach_introduction_call')
                  : coach.orientation_call_completed
                    ? t('select_dedicated_coach_card.tiles.check_in.title')
                    : t(
                        'select_dedicated_coach_card.tiles.check_in.coach_intro_call_title',
                      )
              }
              description={
                callScheduled
                  ? t('coach_meetings_card.upcoming_meeting', {
                      date: scheduledDate,
                    })
                  : t('select_dedicated_coach_card.tiles.check_in.description')
              }
              icon="calendar"
              link={
                coach.orientation_call_completed
                  ? '/coach_services/professional_services/general_check_in'
                  : '/coach_services/professional_services/orientation_call'
              }
            />
            <CardTile
              title={t('select_dedicated_coach_card.tiles.message.title')}
              description={t(
                'select_dedicated_coach_card.tiles.message.description',
              )}
              icon="chat"
              link="/coach_messages"
            />
          </div>
          <div className="grid w-full gap-8 lg:grid-cols-2">
            <CardTile
              title={t('select_dedicated_coach_card.tiles.services.title')}
              description={t(
                'select_dedicated_coach_card.tiles.services.description',
              )}
              icon="coach-services-circle"
              link="/coach_services/professional_services"
            />
            <CardTile
              title={t('select_dedicated_coach_card.tiles.feedback.title')}
              description={t(
                'select_dedicated_coach_card.tiles.feedback.description',
              )}
              icon="star"
              link="/coach_services/feedback"
            />
          </div>
          {coach?.latest_feedback && (
            <CardTile
              title={t(
                'select_dedicated_coach_card.tiles.latest_feedback.title',
              )}
              description={safeParse(quotedFeedback)}
              icon="star"
              link={
                coach.latest_feedback.service_request == 'resume_review'
                  ? `/coach_services/resume_review`
                  : `/coach_services/professional_services/${coach.latest_feedback.service_request}`
              }
              date={formatTime(coach.latest_feedback.completed_at, 'date')}
              linkText={t(
                'select_dedicated_coach_card.tiles.latest_feedback.view_action',
              )}
            />
          )}
        </Stack>
      </div>
      <CoachInfoDialog coach={coach} />
    </div>
  )
}

const CardTile = ({ title, description, icon, link, date, linkText }) => {
  return (
    <a
      href={link}
      className="flex flex-col items-start gap-4 rounded-xl bg-white p-6 transition hover:bg-intoo-blue-light focus:bg-intoo-blue-light active:bg-intoo-blue-light"
    >
      <div className="flex items-center gap-4">
        <div className="rounded-2xl bg-willow-brook p-2">
          <YnsIcon name={icon} className="h-8 w-8 text-primary" />
        </div>
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center justify-between">
            <Typography variant="subtitle">{title}</Typography>
            {date && <Typography variant="caption">{date}</Typography>}
          </div>
          <Typography>{description}</Typography>
          {linkText && (
            <Typography className="!text-primary !underline">
              <a href={link}>{linkText}</a>
            </Typography>
          )}
        </div>
      </div>
    </a>
  )
}

const IntakeCallScheduling = ({
  availability,
  setScheduledIntakeCall,
  onSchedulingError,
}) => {
  const { current_user } = useBenefits()
  const formatTime = useFormatTime(current_user.locale)
  const eventType = availability.event_type
  const startTime = new Date(availability.start_time)
  const endTime = new Date(startTime)
  endTime.setMinutes(endTime.getMinutes() + 30)
  const [openDialog, setOpenDialog] = useState(false)
  const [scheduling, setScheduling] = useState(false)
  const { t } = useTranslation('select_liaison_coach_card')
  const dayOfWeek = formatTime(startTime, 'day_month_year')
  const formattedStartTime = formatTime(startTime, 'time')
  const formattedEndTime = formatTime(endTime, 'time')

  const formattedTime = `${dayOfWeek}, ${formattedStartTime} - ${formattedEndTime}`

  const coachId = availability.coach_id

  const { toast } = useToast()

  const queryClient = useQueryClient()

  const createCalendlyevent = async (res) => {
    const invitee = res?.scheduled_event?.resource
    if (!invitee?.event || !invitee?.uri) {
      throw new Error('Invalid scheduling response from Calendly')
    }
    const calendlyId = invitee.event.split('/').pop()
    const calendlyUserId = invitee.uri.split('/').pop()
    const data = {
      request_format: 'json',
      calendly_event: {
        candidate_id: current_user.candidate_id,
        coach_id: coachId,
        calendly_id: calendlyId,
        calendly_user_id: calendlyUserId,
        meeting_name: '30 Minute Intake Meeting',
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
      },
    }
    const response = await apiPost('/calendly_events.json', data)
    return response
  }

  const handleSchedulingError = () => {
    setScheduling(false)
    setOpenDialog(false)
    queryClient.invalidateQueries(['availableTimes'])
    if (typeof onSchedulingError === 'function') {
      onSchedulingError(availability)
    }
    toast({
      description: t('scheduling_error_description'),
      title: t('scheduling_error_title'),
      variant: 'destructive',
      duration: 5000,
    })
  }

  const createCalendlyEventMutation = useMutation({
    mutationFn: createCalendlyevent,
    onSuccess: (res) => {
      const mergedIntakeCall = {
        ...res.data.intakeCall,
        ...res.data.calendlyEvent,
      }
      setScheduledIntakeCall(mergedIntakeCall)
      queryClient.invalidateQueries(['availableTimes'])
      setOpenDialog(false)
      setScheduling(false)
    },
    onError: (error) => {
      console.error('error scheduling event', error)
      handleSchedulingError()
    },
  })

  const scheduleEvent = async () => {
    setScheduling(true)
    const data = {
      event_type: eventType,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      location: {
        kind: 'zoom_conference',
      },
    }

    let response
    try {
      response = await apiPost(
        '/schedule_select_intake_call',
        JSON.stringify(data),
      )
      if (
        response.status === 200 &&
        response?.data?.scheduled_event?.resource
      ) {
        createCalendlyEventMutation.mutate(response.data)
      } else {
        throw new Error('Failed to schedule event')
      }
    } catch (error) {
      console.error('Error scheduling event', error)
      handleSchedulingError()
    }
  }

  return (
    <Dialog open={openDialog} onOpenChange={() => setOpenDialog(!openDialog)}>
      <DialogTrigger asChild>
        <Button
          className="h-auto min-h-10 whitespace-normal !rounded-xl lg:rounded-full"
          variant="outline"
        >
          {formattedTime}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('schedule_dialog_title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-start gap-4 p-4">
          <div>{t('schedule_dialog_content')}</div>
          <div className="flex gap-4">
            <Button
              component="button"
              onClick={scheduleEvent}
              disabled={scheduling}
            >
              <div className="flex items-center gap-2">
                {t('schedule_dialog_yes')}
                <SpinnerWhite show={scheduling} />
              </div>
            </Button>
            <Button onClick={() => setOpenDialog(false)} variant="outline">
              {t('schedule_dialog_no')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function wrapQuotesInParagraphs(html) {
  if (!html) return ''
  const trimmed = html.trim()
  let result = trimmed

  // Check for starting <p> tag
  if (trimmed.startsWith('<p>')) {
    result = result.replace('<p>', '<p>"')
  } else {
    result = '"' + result
  }

  // Check for ending </p> tag
  if (trimmed.endsWith('</p>')) {
    // Insert quote before closing </p>
    result = result.replace(/<\/p>$/, '"</p>')
  } else {
    result = result + '"'
  }

  return result
}
